<template>
<v-window v-model="window" vertical >
  <request-status :request='mergeReq' />
  <v-breadcrumbs :items='crumbs' />
  <v-window-item >
    <v-card>
      <v-card-title class='subtitle-1 overline text-h5 px-10' >
        Create a new space from other spaces
      </v-card-title>
      <v-card-text class='text-h5 px-10' >
        <p>
          This wizard will guide you through combining some of your existing spaces into a single space
        </p>
        <ul class='font-weight-light' >
          <li class='py-2' >
            You can import settings, and converge locations and appointment types into a single space
          </li>
          <li class='py-2' >
            We will archive (not delete) the old spaces once the new space has been created
          </li>
          <li class='py-2'>
            <strong>This is an advanced action.</strong> If you would prefer,
            <a :href='`mailto:${LINKS.SUPPORT_EMAIL}`' >contact support</a>,
            and we can walk you through the process
          </li>
        </ul>

      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn @click='window++' color='primary' >Get started</v-btn>
      </v-card-actions>

    </v-card>

  </v-window-item >

  <!-- Choose spaces -->
  <v-window-item >
    <v-card>
      <space-picker multiple owner-required
        v-model='selectedSpaces'
        title='Which spaces would you like to merge?' />

      <v-alert type='info' tile dense text border="left" >
        <strong>Note:</strong> You must be an owner of all spaces you wish to merge
      </v-alert>
      <v-card-actions >
        <v-spacer />
        <v-btn @click="chooseProducts" class='primary' >Next</v-btn>
      </v-card-actions>
    </v-card>

  </v-window-item >

  <!-- Choose settings -->
  <v-window-item >
    <v-card>
      <v-card-title >
        Which settings would you like to use?
      </v-card-title>
      <v-card-text>
        You can pick settings from any one of the spaces, or choose to use your personal settings.
        You will be able to change settings later from the edit page

        <!-- <pre v-text='me' /> -->
        <v-list class='elevation-4 mt-2'>
          <v-subheader class='overline' >Use my personal details</v-subheader>
          <!-- P:<pre v-text='profile' /> -->
          <settings-list-item v-if='profile && profile.user' v-model='profile' >
            <v-list-item-action slot='action' >
              <v-btn @click='setSelected("me")' outlined color='primary' >select</v-btn>
            </v-list-item-action>
          </settings-list-item>
          <v-divider/>
          <v-subheader class='overline' >Use details from a space</v-subheader>
          <template v-for='(space, index) in spaceObjects' >
            <settings-list-item v-model='spaceObjects[index]' :key='space.id' >
              <v-list-item-action slot='action' >
                <v-btn outlined color='primary' @click='setSelected(space)' >select</v-btn>
              </v-list-item-action>
            </settings-list-item>
          </template>
        </v-list>

      </v-card-text>
      <v-card-actions >
        <v-btn @click='window--' text >Back</v-btn>
        <v-spacer />
        <v-btn @click="chooseProducts" class='primary' >Next</v-btn>
      </v-card-actions>
    </v-card>

  </v-window-item >

  <!-- choose locations -->
  <v-window-item >
    <v-card>
      <v-card-title class='subtitle-1 overline' >
        Which locations would you like to import?
      </v-card-title>
      <v-card-text>
        <v-checkbox small outlined dense row hide-details return-object multiple
          v-for='location in locations'
          v-model='selectedLocations'
          :key='location.id'
          :value='location'
          :label='location.name'
          class='ma-1' >
        </v-checkbox>
      </v-card-text>
      <v-card-actions >
        <v-btn @click='window--' text ><v-icon>chevron-left</v-icon> Back </v-btn>
        <v-spacer />
        <!-- :disabled='selectedLocations.length === 0' -->
        <v-btn
          color='primary'
          @click='window++' ><v-icon>chevron-left</v-icon>
          Next <v-icon>chevron-left</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-window-item>

  <v-window-item >
    <v-card>
      <v-card-title class='overline' >
        Which locations and appointment types would you like to copy to the new space
      </v-card-title>
      <v-row >
        <v-col cols=6 >
            <template v-for='product in products' >
              <v-card :key='product.id' class='ma-2' >
                <v-card-title>
                  <v-checkbox dense persistent-hint
                    v-model='selectedProductIds'
                    :value='product.id'
                    :label='product.title'
                    :hint='getProductHint(product)'
                    @change='setProduct(product)' />
                </v-card-title>
                <v-card-text v-if='selectedProductIds.indexOf(product.id) >= 0' >

                  <v-divider/>

                  <strong class='caption' >
                    Which locations is this appointment type available at?
                  </strong>

                  <v-checkbox small outlined dense row hide-details return-object multiple
                    v-for='location in selectedLocations'
                    v-model='selectedProducts[product.id].locations'
                    :key='location.id'
                    :value='location'
                    :label='location.name'
                    class='ma-1' >
                  </v-checkbox>

                </v-card-text>
              </v-card>
              <v-divider :key="`div-${product.id}`" />
            </template>

        </v-col>
        <v-col cols=6 >
          <!-- {{space}} -->
          <v-window v-model='summaryWindow' >
            <v-window-item >
            <v-card outlined shaped class='ma-3' elevation=5 >
              <v-card-title class='overline py-0 grey lighten-4' >Summary</v-card-title>
              <v-divider/>
              <v-text-field persistent-hint large filled
                v-model='title'
                label='Title'
                class='ma-3'
                hint='What would you like to call the new space that you are creating?' />

              <template v-if='title && title.length > 0' >
                <v-card-text>
                  <v-card>
                    <v-card-title class='subtitle-1 overline' >
                      We'll combine these spaces
                    </v-card-title>
                    <v-card-text>
                      <v-chip outlined v-for='space in spaceObjects' :key='space.id' class='mr-1 mb-1' volor='success' >
                        <v-icon left >mdi-check</v-icon>
                        {{space.title}}
                      </v-chip>
                    </v-card-text>
                  </v-card>

                  <v-card v-if='locationIds && locationIds.length > 0'>
                    <v-card-title class='grey lighten-4 overline py-0' >
                      We'll copy the following locations/addresses
                    </v-card-title>
                    <v-list dense>
                      <v-list-item v-for='location in selectedLocations' :key=location.id >
                        <v-list-item-content>
                          <v-list-item-title>{{location.name}}</v-list-item-title>
                          <v-list-item-subtitle>{{location.address}}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>

                  <v-card v-if='selectedProductIds && selectedProductIds.length > 0' class='mt-2' >
                    <v-card-title class='grey lighten-4 overline py-0' >
                      We'll copy the following products
                    </v-card-title>
                    <v-divider/>
                    <v-list dense class='pa-0' >
                      <template v-for='product in selectedProducts' >
                        <v-list-item :key='product.id' >
                          <v-list-item-content>
                            <v-list-item-title v-text="product.title" />
                            <v-list-item-subtitle >{{ getProductHint(product) }}
                            </v-list-item-subtitle>
                            <v-card flat >
                              <v-chip x-small outlined label
                                v-for='loc in product.locations'
                                :key='loc.id'
                                class='mt-1 mr-1' >{{loc.name}}</v-chip>
                            </v-card>
                          </v-list-item-content>
                        </v-list-item>
                        <!-- <v-divider :key='`div-${product.id}`' /> -->
                      </template>
                    </v-list>
                  </v-card>

                </v-card-text>
                <v-card-actions >
                  <v-btn @click='window--' text ><v-icon>chevron-left</v-icon> Back </v-btn>
                  <v-spacer />
                  <v-btn @click='merge' color='primary' class='mr-2' >Create new space!</v-btn>
                </v-card-actions>
                <!-- <pre v-text='payload' /> -->
              </template>
            </v-card>
            </v-window-item>

            <v-window-item >
              <v-card>
                <v-card-title class='overline justify-center' >
                  <v-icon>mdi-check-all</v-icon>
                  Spaces merged
                </v-card-title>
                <v-card-text class='text-center' >
                  <v-avatar color='green' >
                    <v-icon color='white' >mdi-call-merge</v-icon>
                  </v-avatar>
                </v-card-text>
                <v-card-text class='text-center' >
                  <v-btn
                    :to='`/settings/spaces/${mergedSpace.id}`'
                    outlined class='mb-1' style='width:150px;' >Edit Space</v-btn><br/>
                  <v-btn to='/' outlined style='width:150px;' >Back home</v-btn>
                </v-card-text>
              </v-card>
            </v-window-item>
          </v-window>
        </v-col>
      </v-row>
    </v-card>
  </v-window-item >


</v-window>
</template>
<script>
import SpacePicker from '@/components/pickers/SpacePicker.vue'
// import SpaceCard from '@/components/cards/SpaceCard.vue'
import SettingsListItem from '@/components/listitems/SettingsListItem.vue'

import {mapGetters} from 'vuex'
import {LINKS} from '@/store/constants.js'
import Vue from 'vue'

export default {
  name: 'ConvergeSpacesWizard',
  components: {SpacePicker, SettingsListItem},
  data () {
    return {
      window: 0,
      summaryWindow: 0,
      selectedSpaces: [],
      selectedProductIds: [],
      selectedProducts: {},
      selectedLocations: [],
      selectedSettings: { "space:": null },
      title: '',
      LINKS: LINKS,
      productReq: {
        resource: 'ag.spaces.products.v3'
      },
      mergeReq: {
        resource: 'ag.spaces.practitioner',
        extra: 'actions/merge_spaces',
        method: 'post'
      },
      profileReq: {
        resource: 'ag.practitioner.profile'
      },
      mergedSpace: {},
      crumbs: [
        {
          text: 'Home',
          disabled: false,
          to: '/',
        },
        {
          text: 'Spaces',
          disabled: false,
          to: '/settings/spaces/',
        },
        {
          text: 'Merge spaces',
          disabled: true
        }
      ]
    }
  },
  mounted () {
    this.$request('get', this.profileReq)
  },
  methods: {
    chooseProducts () {
      this.getProducts()
      this.window++
    },
    getProducts () {
      this.$request('get', this.productReq)
    },
    getProductHint (product) {
      return `${product.duration} mins @ ${ this.formatCurrency(product.currency, product.price) }`
    },
    setSelected (val) {
      if (val !== 'me') {
        this.selectedSettings.space = val
      }
      this.window++
    },
    setProduct (product) {
      if (product.id in this.selectedProducts) {
        delete this.selectedProducts[product.id]
      } else {
        let p = {
          id: product.id,
          title: product.title,
          duration: product.duration,
          currency: product.currency,
          price: product.price,
          locations: []
        }
        Vue.set(this.selectedProducts, product.id, p)
      }
    },
    merge () {
      // let url = '/api/v3/practitioner/spaces/256/actions/merge_spaces/'
      this.mergeReq.data = this.payload
      this.$request('save', this.mergeReq)
    }
  },
  watch: {
    mergeReqStatus () {
      if (this.mergeReqStatus === 200) {
        this.mergedSpace = this.mergeReq.result
        this.summaryWindow = 1
        this.init()
      }
    }
  },
  computed: {
    ...mapGetters('identity', [
      'getLocationsBySpaceId',
      'getSpaceById',
      'meId'
    ]),
    profile () {
      let p = this.getObjectPath(this.profileReq, 'result', [])
      if (p && p.length === 1) { return p[0] }
      return null
    },
    products () {
      return this.getObjectPath(this.productReq, 'result.results', [])
    },
    locations () {
      let collections = this.selectedSpaces.map((spaceId) => {
        return this.getLocationsBySpaceId(spaceId)
      })
      return collections.reduce((acc, val) => {
        return [...acc, ...val];
      }, [])
    },
    locationIds () {
      return this.locations.map((loc) => { return loc.id })
    },
    spaceObjects () {
      return this.selectedSpaces.map((space) => { return this.getSpaceById(space) })
    },
    space () {
      if (this.selectedSpaces && this.selectedSpaces.length > 0) {
        let id = this.selectedSpaces[0]
        return this.getSpaceById(id)
      }
      return null
    },
    mergeReqStatus () {
      return this.mergeReq.status
    },
    formattedProducts () {
      if (this.selectedProducts) {
        let products = Object.values(this.selectedProducts)
        return products.map((product) => {
          return {
            id: product.id,
            locations: product.locations.map((loc) => { return loc.id })
          }
        })
      }
      return []
    },
    payload () {
      return {
        title: this.title,
        actor: this.meId,
        spaces: this.selectedSpaces.join(','),
        products: JSON.stringify(this.formattedProducts),
        locations: this.locationIds.join(',')
      }
    }
  }
}
</script>
